/**
 * Foothold's structures in the D style (DECISIONS 196, 197: *"base it off modern designs"*; 199: good gear beside dated
 * surplus where it is maintainable and ships well) — the bigger mode's bases, as a sheet first (design_sheets/
 * foothold_structures.html). Built as the D guns and vehicles are (rendering/vehicleLooks.ts: side profiles extruded across
 * the part and chamfered, bare, separate moving parts), in recognisable modern forms:
 *   - **the HQ** — an expeditionary operations centre: two shipping containers under a tent fly, a ring of Hesco
 *     barriers with a chicane gate, generators and air-conditioning units, an antenna farm and a satellite dish
 *     (it turns), a marker panel on the roof; today's root mast (hqRoot.ts) stands beside it as now;
 *   - **the landing pad** — a square of aluminium matting sized for the dropship (69.3b), its touchdown circle and edge
 *     lights (they blink), a windsock on its pole, a sandbagged marshaller's post;
 *   - **the FOB** — a Hesco U open to the rear, its stores on pallets under a camouflage net, the radio's mast at the back
 *     corner (its light the web's, as the relay's), perimeter lamps;
 *   - **the beacon** — a tripod and telescoping pole, a rugged transceiver case at its foot, a small dish (it turns) and
 *     a strobe;
 *   - **the supply dump** — two pallets of crated ammunition and jerrycans, strapped, a cargo net, a marker stake; the
 *     load shrinks with the stock (`setSupplyLoad`);
 *   - **the relay** and **the sandbag HMG emplacement** — today's models (relayModel.ts, hmgModel.ts) given the D pass in
 *     place: every block chamfered, the bags laid as filled sandbags (pillowed, tied ends) on today's ring, so their parts,
 *     anchors and fitted hit shapes (the relay's three boxes, the ring's posts, the gunner's seat) do not move.
 *
 * Each in three forms (63.4's pattern: `whole`, `damaged` struck, `destroyed` wrecked in the scorched palette the host
 * passes); the blinking lamps merged to one mesh a structure, the supply dump's crate layers baked a layer each.
 * The owner, Oct 18: *"new models look good enough"* (BUILDLIST 67.62, with the proposed hit shapes for the three that move).
 *
 * Person units (a person is 1.8; world = × personHeight / 1.8). Presentation only: in play through renderer.ts
 * createStructureMesh (the HQ and the pad where buildings are sized by function, the FOB, the beacon, the supply dump, the
 * relay) and the action host's HMG pit.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { bakeStaticParts, markShared } from './meshBake';
import { damageModel } from './damageForms';
import type { UnitPalette } from './unitPalette';
import { box, group, mat } from './vehicleModels';
import { slab, front, top, ring, rect, rev, cyl, type Pts } from './vehicleLooks';
import { windMount } from './buildingKit';
import { buildRelayModel, paintLinkLight, type RelayLink, type RelayModel } from './relayModel';
import { buildHmgModel, buildHmgWreck, type HmgModel } from './hmgModel';
import { buildPlayHmg } from './gunLooks';

export const FOOTHOLD_STRUCTURES = ['hq', 'landingPad', 'fob', 'beacon', 'supply', 'relay', 'hmg'] as const;
export type FootholdStructure = typeof FOOTHOLD_STRUCTURES[number];
/** 63.4's forms: standing, struck (scorched, small parts off, the weak part broken), wrecked (the host passes a scorched palette). */
export type FootholdForm = 'whole' | 'damaged' | 'destroyed';
export const FOOTHOLD_FORMS: FootholdForm[] = ['whole', 'damaged', 'destroyed'];

const STEEL = '#5e615e', DARK = '#26282a', GLASS = '#1c2328', SAND = '#b6a47c', SAND_DARK = '#9a8a66', WIRE = '#5a5a52';
const OLIVE = '#5a5d3c', OLIVE_DARK = '#3f4229', CANVAS = '#7d7656', TAN = '#a8956a', WOOD = '#8a6a42', MATTING = '#5b5f5e', PAINT = '#e8e4d4';
const AMBER = '#ffb347';

/** A built structure: its root (scaled), its moving parts for the sheet and the host, and what it costs. */
export interface FootholdModel {
  kind: FootholdStructure;
  form: FootholdForm;
  root: THREE.Group;
  /** turned about y by `spin` (the HQ's and the beacon's dishes) */
  dishes: THREE.Group[];
  /** lamps the host lights (the web's link: the FOB's mast): never baked */
  lamps: THREE.Mesh[];
  /** the blinking lamps (the pad's edge lights, the FOB's perimeter lamps): one merged mesh each, its own material */
  blink: THREE.Mesh[];
  /** the supply dump's crate layers, bottom first (`setSupplyLoad` hides them from the top down; each bakes to one mesh) */
  crates: THREE.Group[];
  /** the relay or the HMG when the structure is today's model given the D pass */
  relay?: RelayModel;
  hmg?: HmgModel;
}

const lampMat = (color: string) => markShared(new THREE.MeshLambertMaterial({ color, emissive: new THREE.Color(color), emissiveIntensity: 0.9 }));
function lamp(m: FootholdModel, parent: THREE.Object3D, r: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const l = new THREE.Mesh(new THREE.BoxGeometry(r, r, r), lampMat(color));
  l.position.set(x, y, z); l.userData.noBake = true; parent.add(l); m.lamps.push(l);
  return l;
}

/** A blinking lamp: gathered under `into` (merged by `mergeBlink` once the structure is built). */
function blinker(into: THREE.Group, r: number, x: number, y: number, z: number): void {
  const l = new THREE.Mesh(new THREE.BoxGeometry(r, r, r)); l.position.set(x, y, z); into.add(l);
}
/** Merge a structure's blinking lamps into one mesh with its own amber material (they blink together: one draw). */
function mergeBlink(m: FootholdModel, into: THREE.Group): void {
  if (!into.children.length) { into.removeFromParent(); return; }
  m.root.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(m.root.matrixWorld).invert(), geos: THREE.BufferGeometry[] = [];
  into.traverse(o => { const mesh = o as THREE.Mesh; if (mesh.isMesh) geos.push(mesh.geometry.clone().applyMatrix4(new THREE.Matrix4().multiplyMatrices(inv, mesh.matrixWorld))); });
  into.removeFromParent();
  const merged = new THREE.Mesh(mergeGeometries(geos), new THREE.MeshLambertMaterial({ color: AMBER, emissive: new THREE.Color(AMBER), emissiveIntensity: 0.9 }));
  merged.name = 'blink'; merged.userData.noBake = true;
  m.root.add(merged); m.blink.push(merged);
}
/** A seeded stream (a wreck's scatter). */
function rng(seed: number): () => number { let s = (seed * 2654435761) >>> 0 || 1; return () => { s ^= s << 13; s >>>= 0; s ^= s >>> 17; s ^= s << 5; s >>>= 0; return s / 4294967296; }; }
const dishMat = markShared(new THREE.MeshLambertMaterial({ color: PAINT, side: THREE.DoubleSide, flatShading: true }));

/** A strut from `a` to `b`, `t` thick (a unit box stretched and turned onto the span). */
function strut(parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, t: number, color: string): THREE.Mesh {
  const d = b.clone().sub(a), m = box(parent, t, 1, t, color, 0, 0, 0);
  m.position.copy(a).addScaledVector(d, 0.5); m.scale.y = d.length();
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize());
  return m;
}

// ---------------------------------------------------------------------------
// Parts shared by the bases
// ---------------------------------------------------------------------------

/**
 * A run of Hesco barriers from (x0, z0) to (x1, z1): wire-mesh bastions a metre square, filled, `h` tall — each a
 * chamfered block of fill with its frame's posts and a top rail.
 */
function hesco(parent: THREE.Object3D, x0: number, z0: number, x1: number, z1: number, h = 1.35, size = 1.05, broken?: (i: number) => 'gone' | 'slumped' | null): void {
  const len = Math.hypot(x1 - x0, z1 - z0), n = Math.max(1, Math.round(len / size)), cell = len / n;
  const g = group(parent, x0, 0, z0); g.rotation.y = Math.atan2(x1 - x0, z1 - z0);
  for (let i = 0; i < n; i++) {
    const z = (i + 0.5) * cell, b = broken?.(i) ?? null;
    if (b === 'gone') { slab(g, rect(z - cell / 2, 0, z + cell / 2, 0.25), size * 1.3, SAND_DARK, 0, 0.08); continue; }   // (its fill spilled where it stood)
    const hh = b === 'slumped' ? h * 0.62 : h;
    const fill = slab(g, rect(z - cell / 2 + 0.02, 0, z + cell / 2 - 0.02, hh - 0.04), size - 0.06, i % 3 === 1 ? SAND_DARK : SAND, 0, 0.04);
    if (b === 'slumped') fill.rotation.z = 0.12;
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, 0.035, hh, 0.035, WIRE, sx * (size / 2 - 0.02), hh / 2, z + sz * (cell / 2 - 0.02));
  }
  if (!broken) for (const sx of [-1, 1]) box(g, 0.03, 0.03, len, WIRE, sx * (size / 2 - 0.02), h - 0.015, len / 2);
}
/** A wall's breaks for a form: none standing; a slumped bastion here and there struck; bites blown out wrecked. */
function breaks(form: FootholdForm, r: () => number): ((i: number) => 'gone' | 'slumped' | null) | undefined {
  if (form === 'whole') return undefined;
  return () => { const x = r(); return form === 'destroyed' ? (x < 0.3 ? 'gone' : x < 0.55 ? 'slumped' : null) : (x < 0.12 ? 'slumped' : null); };
}

/** A 20 ft shipping container (6.06 × 2.44 × 2.59 m), its long axis along z, ribbed sides, doors at −z. */
function container(parent: THREE.Object3D, p: UnitPalette, x: number, z: number, color = OLIVE, ry = 0): THREE.Group {
  const g = group(parent, x, 0, z); g.rotation.y = ry;
  const L = 6.06, W = 2.44, H = 2.59;
  slab(g, rect(-L / 2, 0.08, L / 2, H), W, color, 0, 0.05);
  for (const sx of [-1, 1]) {
    for (let k = 0; k < 11; k++) box(g, 0.04, H - 0.32, 0.12, color, sx * (W / 2 + 0.01), H / 2 + 0.02, -L / 2 + 0.35 + k * ((L - 0.7) / 10));   // the corrugation
    box(g, 0.05, 0.12, L, DARK, sx * (W / 2 - 0.02), 0.06, 0);                                                                           // the bottom rail
  }
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, 0.16, 0.16, 0.16, DARK, sx * (W / 2 - 0.06), sz < 0 ? 0.08 : H - 0.08, -L / 2 + 0.08);   // corner castings
  for (const sx of [-0.55, -0.2, 0.2, 0.55]) box(g, 0.04, H - 0.3, 0.04, STEEL, sx, H / 2, -L / 2 - 0.03);   // the door bars
  front(g, rect(-W / 2 + 0.1, H - 0.55, W / 2 - 0.1, H - 0.3), 0.02, p.body, -L / 2 - 0.02);                 // the team's band across the doors
  return g;
}

/** A pallet (1.2 m × `depth`, its deck boards). */
function pallet(parent: THREE.Object3D, x: number, z: number, ry = 0, depth = 1.0): THREE.Group {
  const g = group(parent, x, 0, z); g.rotation.y = ry;
  for (const sx of [-0.5, 0, 0.5]) box(g, 0.1, 0.1, depth, WOOD, sx, 0.05, 0);
  const n = Math.round(depth / 0.2);
  for (let k = 0; k < n; k++) box(g, 1.2, 0.025, 0.14, WOOD, 0, 0.115, -depth / 2 + 0.08 + k * ((depth - 0.16) / (n - 1)));
  return g;
}

/** An ammunition crate: a chamfered wooden box, its rope handles and a stencil band. */
function crate(parent: THREE.Object3D, w: number, h: number, d: number, x: number, y: number, z: number, color = OLIVE): THREE.Mesh {
  const m = slab(parent, rect(-d / 2, 0, d / 2, h), w, color, x, 0.02);
  m.position.set(x, y, z);
  box(m, w + 0.01, 0.04, 0.12, PAINT, 0, h * 0.62, 0);
  return m;
}

/** A jerrycan (the NATO 20 l can): a chamfered slab with its handles. */
function jerrycan(parent: THREE.Object3D, x: number, z: number, color = OLIVE_DARK): void {
  slab(parent, [-0.17, 0, 0.17, 0, 0.17, 0.42, 0.1, 0.47, -0.17, 0.47], 0.16, color, x, 0.015).position.z = z;
  box(parent, 0.04, 0.05, 0.2, DARK, x, 0.5, z);
}

/** A sandbag laid along z (the pillow: tapered ends, a crowned top), `w` long × `h` × `d` (REFERENCE.sandbag). */
const pillows = new Map<string, THREE.BufferGeometry>();
function pillowGeometry(w: number, h: number, d: number): THREE.BufferGeometry {
  const key = `${w}|${h}|${d}`;
  let g = pillows.get(key);
  if (!g) {
    // the bag's section across its length (x, y): a crowned top over flattened sides; extruded along its length, chamfered
    const tmp = new THREE.Group(), hd = d / 2;
    const m = front(tmp, [-hd, 0, hd, 0, hd, h * 0.62, hd * 0.6, h, -hd * 0.6, h, -hd, h * 0.62], w, SAND, 0, Math.min(0.06, w * 0.12));
    g = m.geometry; g.rotateY(Math.PI / 2); g.translate(0, -h / 2, 0);   // (centred as the box it replaces: its length along x)
    markShared(g); pillows.set(key, g);
  }
  return g;
}

/** The D pass on a built model: every box block over `min` chamfered in place (same size, same place, same parent). */
const chamfered = new Map<string, THREE.BufferGeometry>();
export function chamferBlocks(root: THREE.Object3D, min = 0.06, c = 0.18, skip?: THREE.Object3D): number {
  let n = 0;
  const skipped = new Set<THREE.Object3D>(); skip?.traverse(o => skipped.add(o));
  root.traverse(o => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || skipped.has(o) || !(mesh.geometry instanceof THREE.BoxGeometry) || o.userData.noBake) return;
    const { width: w, height: h, depth: d } = mesh.geometry.parameters;
    if (Math.min(w, h, d) < min) return;
    const key = `${w}|${h}|${d}|${c}`;
    let g = chamfered.get(key);
    if (!g) {
      const tmp = new THREE.Group(), e = Math.min(w, h, d) * c;
      g = slab(tmp, rect(-d / 2, -h / 2, d / 2, h / 2), w, '#000', 0, e).geometry;
      markShared(g); chamfered.set(key, g);
    }
    mesh.geometry = g; n++;
  });
  return n;
}

// ---------------------------------------------------------------------------
// The structures
// ---------------------------------------------------------------------------

/** The HQ: a Hesco square (a chicane gate at −z, the side's own lines), two containers under a tent fly, the plant, the antennas. */
function hq(m: FootholdModel, p: UnitPalette): void {
  const g = m.root, H = 6.6, f = m.form, r = rng(7), br = breaks(f, r);
  // the gravel pad and its apron behind the gate, under the lane and the chicane wall (63.16 (3)'s follow-up: within the
  // plinth's deck, 10.03) — floors, no target (the hit fit's coverage leaves them out)
  top(g, rect(-7.2, -7.2, 7.2, 7.2), 0.12, '#8c8676', 0, 0.04).userData.thin = true;
  top(g, rect(-3.3, -10.0, 3.3, -7.2), 0.12, '#8c8676', 0, 0.04).userData.thin = true;
  hesco(g, -H, H, H, H, 1.35, 1.05, br); hesco(g, H, H, H, -H, 1.35, 1.05, br); hesco(g, -H, -H, -H, H, 1.35, 1.05, br);   // three sides
  hesco(g, H, -H, 1.6, -H, 1.35, 1.05, br); hesco(g, -H, -H, -1.6, -H, 1.35, 1.05, br);               // the back, its gate
  hesco(g, -2.6, -H - 2.8, 2.6, -H - 2.8, 1.35, 1.05, br);                                             // the chicane wall (63.16 (3): 2.8 m behind the rear, was 1.6 — a person needs 0.64 people between the bastions' faces, 1.6 m left 0.31; 2.8 leaves 0.98)
  // the operations centre: two containers, a tent fly between and over them, its front open under an awning
  container(g, p, -2.3, 0.6);
  const right = container(g, p, 2.3, 0.6, OLIVE_DARK);
  if (f === 'destroyed') { right.rotation.z = -0.22; right.position.set(2.6, 0.3, 0.9); right.rotation.y = 0.12; }   // (blown off its footing, tipped)
  if (f !== 'destroyed') {
    const fly = group(g, 0, 0, 0.6);
    slab(fly, rect(-3.3, 2.62, 3.3, 2.7), 7.4, CANVAS);                                                // the fly's deck over both
    front(fly, [-3.7, 2.7, 3.7, 2.7, 0, 3.55], f === 'damaged' ? 4.2 : 6.6, CANVAS, f === 'damaged' ? 1.2 : 0, 0.03);   // the ridge roof (torn back, struck)
    front(fly, rect(-1.0, 0.1, 1.0, 2.6), 6.0, '#3a3a30', 0);                                         // the shadowed walk between
    const awning = slab(fly, [-3.3, 2.65, -4.9, 2.2, -4.9, 2.14, -3.3, 2.59], 3.4, CANVAS, 0);        // the awning at the front (−z)
    if (f === 'damaged') { awning.rotation.z = 0.25; awning.position.y = -0.4; }
    for (const sx of [-1.6, 1.6]) box(fly, 0.06, 2.2, 0.06, STEEL, sx, 1.1, -4.8);                     // its poles
    if (f === 'whole') top(g, rect(-1.0, -0.6, 1.0, 1.4), 0.03, p.flash, 3.56);                        // the marker panel on the ridge (from the air)
  } else {
    slab(g, [-3.8, 2.6, 3.4, 2.6, 4.2, 1.4, 4.1, 1.3, -3.6, 2.5], 3.6, CANVAS, -1.6);                   // the fly, collapsed over one container
    slab(g, rect(-1.2, 0, 1.6, 0.2), 2.4, CANVAS, 0.8).rotation.y = 0.4;                               // and on the ground
  }
  // the plant: two generators and the air-conditioning units on the containers' outsides
  for (const [x, z] of [[-5.1, -3.6], [-5.1, -1.8]] as const) {
    const gen = slab(g, rect(z - 0.7, 0.05, z + 0.7, 1.2), 1.0, OLIVE_DARK, x, 0.05);
    if (f === 'destroyed' && z < -2) gen.rotation.x = 0.5;
    cyl(g, 0.06, 0.5, DARK, x + 0.25, 1.4, z + 0.4, 'y', 6);
  }
  for (const sx of [-1, 1]) for (const z of [-1.4, 2.4]) {
    if (f !== 'whole' && sx > 0 && z > 0) continue;                                                    // (one knocked off)
    slab(g, rect(z - 0.45, 0.9, z + 0.45, 1.6), 0.5, TAN, sx * 3.8, 0.04);
    box(g, 0.02, 0.5, 0.6, DARK, sx * 4.06, 1.25, z);                                                  // its grille
  }
  // the antenna farm: whips on the containers' roofs, a mast with its dish (turning) behind
  if (f !== 'destroyed') for (const [x, z] of [[-3.2, 3.2], [3.2, 3.2], [-1.4, 3.4]] as const) box(g, 0.03, 3.4, 0.03, DARK, x, 2.59 + 1.7, z).userData.thin = true;
  const mast = group(g, 4.6, 0, 4.6);
  if (f === 'destroyed') { mast.rotation.z = -1.35; mast.position.y = 0.2; }                         // (down)
  else if (f === 'damaged') mast.rotation.x = 0.18;
  for (let i = 0; i < 3; i++) { const a = (i / 3) * Math.PI * 2; strut(mast, new THREE.Vector3(Math.sin(a) * 0.9, 0, Math.cos(a) * 0.9), new THREE.Vector3(0, 1.6, 0), 0.06, STEEL); }
  box(mast, 0.12, 2.6, 0.12, STEEL, 0, 1.3, 0);
  const dish = group(mast, 0, 2.6, 0); if (f === 'whole') m.dishes.push(dish);
  const bowl = new THREE.Mesh(new THREE.CylinderGeometry(0.75, 0.2, 0.28, 12, 1, true), dishMat); bowl.rotation.x = -Math.PI / 2 + 0.6; bowl.position.set(0, 0.35, 0.1); dish.add(bowl);
  box(dish, 0.04, 0.04, 0.7, STEEL, 0, 0.55, 0.35).rotation.x = -0.6;
  // a flag in the team's colour at the gate; the camouflage net over the plant
  if (f !== 'destroyed') { box(g, 0.05, 3.2, 0.05, STEEL, 1.9, 1.6, -H - 0.2); slab(g, rect(-0.6, 2.4, 0, 3.15), 0.02, p.body, 1.9).position.z = -H - 0.2; }
  slab(g, rect(-4.4, 2.0, -0.9, 2.05), 1.8, OLIVE_DARK, -5.1).rotation.x = f === 'whole' ? 0 : 0.3;
  for (const z of [-4.3, -1.1]) box(g, 0.05, 2.0, 0.05, STEEL, -5.9, 1.0, z);
}

/** The landing pad: matting 14 × 14 (the dropship's footprint and a margin), its circle and marks, edge lights, a windsock. */
function landingPad(m: FootholdModel, p: UnitPalette): void {
  const g = m.root, S = 7, f = m.form, r = rng(5), lamps = group(g);
  if (f !== 'destroyed') {
    top(g, rect(-S, -S, S, S), 0.08, MATTING, 0, 0.03);
    for (let k = -6; k <= 6; k++) box(g, 2 * S - 0.1, 0.012, 0.03, DARK, 0, 0.086, k * 1.0);        // the matting's planks
  } else {
    // wrecked: the matting torn into panels, two heaved up at the blast, the circle broken with them
    for (const [x0, z0, x1, z1, tx, tz] of [[-S, -S, -0.4, -0.2, 0, 0], [0.6, -S, S, -0.8, 0.18, 0], [-S, 0.4, -0.9, S, 0, -0.14], [0.2, 0.3, S, S, 0, 0]] as const) {
      const pane = top(g, rect(x0, z0, x1, z1), 0.08, MATTING, 0, 0.03); pane.rotation.set(tx, 0, tz);
    }
  }
  // the touchdown circle and the side's mark in it
  top(g, ring(0, 0, 4.2, 32), 0.01, PAINT, 0.088, 0, [rev(ring(0, 0, 3.85, 32))]);
  top(g, rect(-1.6, -0.25, 1.6, 0.25), 0.01, PAINT, 0.088);
  for (const sx of [-1, 1]) top(g, rect(sx * 1.6 - 0.25, -1.9, sx * 1.6 + 0.25, 1.9), 0.01, PAINT, 0.088);
  if (f === 'whole') top(g, rect(-S + 0.4, -S + 0.4, -S + 2.0, -S + 1.2), 0.01, p.flash, 0.088);       // the side's panel at the corner
  // edge lights: four a side, blinking together (one mesh); struck, some out; wrecked, none
  for (let k = 0; k < 4; k++) {
    const t = -S + 1 + k * ((2 * S - 2) / 3);
    for (const [x, z] of [[t, -S], [t, S], [-S, t], [S, t]] as const) {
      box(g, 0.2, 0.08, 0.2, DARK, x, 0.12, z);
      if (f === 'whole' || (f === 'damaged' && r() > 0.35)) blinker(lamps, 0.1, x, 0.2, z);
    }
  }
  // the windsock's pole off the front-right corner (the scene's wind turns it: windMount), a marshaller's sandbag post
  const pole = group(g, S + 1.2, 0, S + 1.2);
  if (f === 'destroyed') pole.rotation.z = 1.45; else if (f === 'damaged') pole.rotation.z = 0.2;
  box(pole, 0.06, 3.6, 0.06, STEEL, 0, 1.8, 0);
  if (f !== 'destroyed') pole.add(windMount('sock', 0, 3.55, 0));
  for (let k = 0; k < 8; k++) {
    const a = (k % 4 / 4) * Math.PI * 1.2 + 0.9, gone = f === 'destroyed' && k >= 4;
    const b = new THREE.Mesh(pillowGeometry(0.5, 0.33, 0.62), mat(SAND));
    b.position.set(-S - 1.6 + Math.sin(a) * 0.9 * (gone ? 1.8 : 1), gone ? 0.12 : 0.165 + Math.floor(k / 4) * 0.33, -S + 2 + Math.cos(a) * 0.9 * (gone ? 1.8 : 1));
    b.rotation.y = a + Math.PI / 2; g.add(b);
  }
  mergeBlink(m, lamps);
}

/** The FOB: a Hesco U open to the rear, pallets of stores under a net, the radio's mast and lamps, inside FOB_MODEL_SIZE. */
function fob(m: FootholdModel, p: UnitPalette): void {
  const g = m.root, I = 1.35, h = 1.2, f = m.form, r = rng(3), br = breaks(f, r), lamps = group(g);   // (the walls' outside at I + 0.5 + 0.475 = 2.33: FOB_MODEL_SIZE.half)
  hesco(g, -I - 0.5, -I, -I - 0.5, I + 0.5, h, 0.95, br); hesco(g, I + 0.5, I + 0.5, I + 0.5, -I, h, 0.95, br); hesco(g, -I - 0.5, I + 0.5, I + 0.5, I + 0.5, h, 0.95, br);
  // the stores: two pallets of crates, jerrycans, under a net on four poles (wrecked: the crates tumbled, the net down on them)
  for (const [x, z] of [[-0.65, 0.55], [0.65, 0.55]] as const) {
    pallet(g, x, z, Math.PI / 2);
    crate(g, 0.55, 0.42, 0.95, x - 0.3, 0.13, z); crate(g, 0.55, 0.42, 0.95, x + 0.3, 0.13, z, OLIVE_DARK);
    if (f === 'whole') crate(g, 0.55, 0.42, 0.95, x, 0.55, z);
    else if (f === 'destroyed') { const c = crate(g, 0.55, 0.42, 0.95, x + (r() - 0.5) * 1.2, 0, z - 1.2 - r() * 0.4); c.rotation.set(0, r() * 2, 1.3); }
  }
  for (const x of [-1.15, -0.95, -0.75]) jerrycan(g, x, -0.75);
  if (f !== 'destroyed') {
    for (const [x, z] of [[-1.25, -0.3], [1.25, -0.3], [-1.25, 1.55], [1.25, 1.55]] as const) box(g, 0.05, 1.75, 0.05, STEEL, x, 0.875, z);
    const net = slab(g, [-0.4, 1.7, 1.65, 1.78, 1.65, 1.82, -0.4, 1.74], 2.6, OLIVE_DARK, 0);          // the net, sagging toward the front
    if (f === 'damaged') { net.rotation.x = 0.2; net.position.y = -0.3; }
    if (f === 'whole') top(g, rect(-0.5, 0.2, 0.5, 0.9), 0.02, p.flash, 0.98);                         // the marker panel on the stack
  } else slab(g, [-0.4, 0.6, 1.65, 0.5, 1.65, 0.54, -0.4, 0.64], 2.6, OLIVE_DARK, 0);
  // the radio's mast at the back corner: three sections, a yagi, the web's light on top; a lamp on each front corner post
  const mast = group(g, 1.05, 0, -1.05);
  slab(mast, rect(-0.22, 0, 0.22, 0.34), 0.6, p.body, 0, 0.03);                                        // the radio's case
  const up = group(mast, 0, 0.34, 0);
  if (f === 'destroyed') { up.rotation.z = 1.5; up.position.y = 0.2; } else if (f === 'damaged') up.rotation.z = 0.12;
  for (let i = 0; i < 3; i++) box(up, 0.08 - i * 0.015, 1.05, 0.08 - i * 0.015, STEEL, 0, 0.5 + i * 0.97, 0);
  box(up, 0.035, 0.035, 0.6, STEEL, 0, 2.81, 0.2); for (let k = 0; k < 4; k++) box(up, 0.28 - k * 0.03, 0.02, 0.02, STEEL, 0, 2.81, k * 0.14);
  lamp(m, up, 0.12, p.flash, 0, 3.08, 0);
  for (const x of [-I - 0.5, I + 0.5]) { box(g, 0.05, 0.5, 0.05, STEEL, x, h + 0.25, I + 0.5); if (f === 'whole') blinker(lamps, 0.08, x, h + 0.52, I + 0.5); }
  mergeBlink(m, lamps);
}

/** The beacon: tripod, pole, transceiver case, dish, strobe — inside today's measured box (HITBOXES.beacon). */
function beacon(m: FootholdModel, p: UnitPalette): void {
  const g = m.root, f = m.form;
  const stand = group(g);
  if (f === 'destroyed') { stand.rotation.z = 1.42; stand.position.set(0.1, 0.1, 0); }                // (knocked flat)
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + 0.3;
    strut(stand, new THREE.Vector3(Math.cos(a) * 0.27, 0, Math.sin(a) * 0.27), new THREE.Vector3(0, 0.85, 0), 0.04, STEEL);
    box(stand, 0.07, 0.02, 0.07, DARK, Math.cos(a) * 0.27, 0.01, Math.sin(a) * 0.27);                 // its foot
  }
  box(stand, 0.09, 0.08, 0.09, DARK, 0, 0.85, 0);                                                    // the collar
  cyl(stand, 0.03, 1.0, STEEL, 0, 0.75, 0, 'y', 8);
  const upper = group(stand, 0, 1.25, 0); if (f === 'damaged') upper.rotation.x = 0.45;               // (bent at the joint, struck)
  cyl(upper, 0.022, 0.85, STEEL, 0, 0.35, 0, 'y', 8);
  const dish = group(upper, 0, 0.53, 0); if (f === 'whole') m.dishes.push(dish);
  const bowl = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.05, 0.09, 10, 1, true), dishMat); bowl.rotation.x = -Math.PI / 2 + 0.4; bowl.position.z = 0.07; dish.add(bowl);
  lamp(m, upper, 0.07, p.flash, 0, 0.75, 0);
  const caseM = slab(g, rect(-0.12, 0, 0.12, 0.24), 0.34, p.body, 0.16, 0.025); caseM.position.z = 0.1;  // the transceiver case
  if (f === 'destroyed') { caseM.rotation.set(0.3, 0.6, 1.1); caseM.position.set(0.3, 0.05, 0.25); }
  box(g, 0.36, 0.03, 0.04, DARK, 0.16, 0.12, 0.225);
}

/** The supply dump: a pallet of crated ammunition, strapped, a cargo net, jerrycans, a marker stake — inside today's measured box (HITBOXES.supply: the crate stack's). */
function supply(m: FootholdModel, p: UnitPalette): void {
  const g = m.root, f = m.form, r = rng(11);
  pallet(g, 0.05, 0, 0, 0.8);
  // (wrecked, two layers still stand, the upper knocked askew: the ruin blocks at BODIES.supply.ruinHeight, 0.55 of the standing height)
  const layers = f === 'whole' ? 3 : 2;
  for (let l = 0; l < 3; l++) {
    const layer = group(g); m.crates.push(layer);
    if (l >= layers) { layer.visible = false; continue; }
    if (f === 'destroyed' && l === 1) { layer.rotation.set(0, 0.45, 0.1); layer.position.set(0.12, -0.03, 0.06); }
    for (const [x, z] of [[-0.22, -0.2], [0.32, -0.2], [-0.22, 0.2], [0.32, 0.2]] as const) crate(layer, 0.52, 0.33, 0.38, x, 0.13 + l * 0.34, z, l === 2 ? OLIVE_DARK : OLIVE);
  }
  if (f !== 'whole') {
    // the struck top layer, or the wreck's: crates thrown off round the pallet, one burst open
    for (let k = 0; k < (f === 'destroyed' ? 5 : 2); k++) {
      const a = r() * Math.PI * 2, d = 0.9 + r() * 0.5;
      const c = crate(g, 0.52, f === 'destroyed' && k === 0 ? 0.16 : 0.33, 0.48, Math.sin(a) * d, 0, Math.cos(a) * d, OLIVE);
      c.rotation.set(0, r() * 3, r() > 0.5 ? 1.57 : 0);
    }
  }
  if (f === 'whole') {
    for (const sx of [-0.25, 0.35]) box(g, 0.02, 0.03, 0.8, '#c0b080', sx, 1.16, 0);                  // the straps over the top
    slab(g, [-0.4, 1.17, 0.4, 1.17, 0.4, 1.2, -0.4, 1.2], 1.1, OLIVE_DARK, 0.05);                      // the cargo net
  } else slab(g, [-0.6, 0.02, 0.5, 0.02, 0.5, 0.05, -0.6, 0.05], 1.1, OLIVE_DARK, -0.9).rotation.y = 0.5;   // (the net thrown off)
  for (const z of [-0.2, 0, 0.2]) jerrycan(g, -0.62, z);
  if (f !== 'destroyed') {
    const stake = box(g, 0.035, 1.55, 0.035, STEEL, 0.6, 0.775, 0.3); stake.userData.thin = true;   // the marker stake at a corner (thin: no target)
    const flag = slab(g, rect(-0.3, 1.3, 0, 1.52), 0.02, p.flash, 0.6); flag.position.z = 0.3; flag.userData.thin = true;
  }
}

/** The relay's D pass, in place on a relay built by `buildRelayModel` (before its bake): every block chamfered — its parts, stages, link light and hit shape as built. */
export function dressRelayD(r: RelayModel): void { chamferBlocks(r.full, 0.06, 0.2); }

/** The relay: today's model (relayModel.ts) in its form with the D pass. */
function relay(m: FootholdModel, p: UnitPalette): void {
  const r = buildRelayModel(p, 1.8, { form: m.form });
  dressRelayD(r);
  m.root.add(r.root); m.relay = r;
}

/** Lay a ring's bags as filled sandbags (the same size, the same places): the tagged ones, and a wreck's burst ones. */
function pillowBags(root: THREE.Object3D): void {
  root.traverse(o => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !(mesh.geometry instanceof THREE.BoxGeometry)) return;
    const { width, height, depth } = mesh.geometry.parameters;
    if (o.userData.sandbag || (width === 0.5 && depth > 0.6)) mesh.geometry = pillowGeometry(width, height, depth);   // (a wreck's burst bags are untagged)
  });
}
/**
 * The HMG emplacement's D pass, in place on one built by `buildHmgModel` (before its bake): the ring laid as filled
 * sandbags on the same places, its blocks chamfered (the gun is the gun lane's D already), a firing step and a range stake
 * (thin: the hit shape leaves them out, as the cans) — its ring, traverse, cradle and the gunner's seat as built.
 */
export function dressHmgD(h: HmgModel): void {
  pillowBags(h.full);
  chamferBlocks(h.full, 0.09, 0.15, h.traverse);
  const step = slab(h.full, rect(-1.3, 0, -0.75, 0.06), 0.9, WOOD, 0, 0.01); step.userData.thin = true;
  const stake = box(h.full, 0.04, 0.5, 0.04, WOOD, 0.35, 0.25, 1.75); stake.userData.thin = true;
}
/** The D pass on the HMG's wreck (`buildHmgWreck`, before any bake): its bags, burst or standing, as filled sandbags. */
export function dressHmgWreckD(w: THREE.Object3D): void { pillowBags(w); chamferBlocks(w, 0.09, 0.15); }

/** The HMG emplacement: today's model (hmgModel.ts) with the D pass; struck, scorched and knocked about (63.4's pattern); wrecked, today's wreck. */
function hmg(m: FootholdModel, p: UnitPalette): void {
  if (m.form === 'destroyed') {
    const w = buildHmgWreck(p, 1.8, 3, buildPlayHmg(p));
    dressHmgWreckD(w);
    m.root.add(w);
    return;
  }
  const h = buildHmgModel(p, 1.8, buildPlayHmg(p));
  dressHmgD(h);
  if (m.form === 'damaged') damageModel(h.full, 5, 0.5, [h.traverse, h.seat]);
  m.root.add(h.root); m.hmg = h;
}

const BUILD: Record<FootholdStructure, (m: FootholdModel, p: UnitPalette) => void> = { hq, landingPad, fob, beacon, supply, relay, hmg };

/** Build a Foothold structure in the D look in `form`, `personHeight` world units a person (a wreck: pass the scorched palette). */
export function buildFootholdLook(kind: FootholdStructure, p: UnitPalette, personHeight: number, form: FootholdForm = 'whole'): FootholdModel {
  const root = new THREE.Group(); root.scale.setScalar(personHeight / 1.8);
  const m: FootholdModel = { kind, form, root, dishes: [], lamps: [], blink: [], crates: [] };
  BUILD[kind](m, p);
  // 63.4's pattern for the new builds: struck, scorched with small parts knocked off; wrecked, scorched through
  if (form !== 'whole' && !m.relay && !m.hmg && kind !== 'hmg') damageModel(root, 7 + FOOTHOLD_STRUCTURES.indexOf(kind), form === 'destroyed' ? 1 : 0.5, [...m.lamps, ...m.blink, ...m.dishes, ...m.crates]);
  return m;
}

/** The supply dump's load: `k` of its crate layers showing (0 … 1), hidden from the top down (a layer is one mesh). */
export function setSupplyLoad(m: FootholdModel, k: number): void {
  const n = Math.ceil(Math.max(0, Math.min(1, k)) * m.crates.length - 1e-6);
  m.crates.forEach((c, i) => { c.visible = i < n; });
}

/** Turn the dishes and blink the amber lamps at `t` seconds (the web lamps are the host's: lightWeb). */
export function animateFoothold(m: FootholdModel, t: number): void {
  for (const d of m.dishes) d.rotation.y = 0.6 * Math.sin(t * 0.25);
  for (const b of m.blink) b.visible = Math.sin(t * 3) > -0.2;
}

/**
 * A structure's lights by the frame (the host's `lightWeb`): its web lamps (the FOB's mast) under the web's rule, as a relay's
 * (paintLinkLight: lit linked, the amber blink unlinked, a flicker struck, dark wrecked or off); its dishes and amber lamps by
 * `animateFoothold`. `p` the palette it was built in.
 */
export function footholdLights(m: FootholdModel, p: UnitPalette): (link: RelayLink, t: number) => void {
  const web = m.lamps.map(l => ({ lightMat: l.material as THREE.MeshLambertMaterial, palette: p, shown: undefined as string | undefined }));
  return (link, t) => {
    for (const w of web) paintLinkLight(w, m.form === 'damaged', m.form !== 'destroyed' && link !== 'off', link, t);
    animateFoothold(m, t);
  };
}

/**
 * Bake the static parts: the structure as one, each dish on its own; the crate layers on their own when the load moves
 * (`liveLoad`: the sheet; play draws the load full, so they fold in); the lamps never.
 */
export function bakeFootholdLook(m: FootholdModel, liveLoad = true): number {
  if (m.relay || m.hmg) return 0;   // (today's models bake with their own bakers)
  const keep = [...m.dishes, ...(liveLoad ? m.crates : [])];
  let n = bakeStaticParts(m.root, { exclude: keep }).partsBaked;
  for (const g of keep) n += bakeStaticParts(g).partsBaked;
  return n;
}

/** Triangles and draws as drawn (each mesh one draw). */
export function footholdCost(root: THREE.Object3D): { triangles: number; draws: number } {
  let triangles = 0, draws = 0;
  root.traverse(o => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    for (let q: THREE.Object3D | null = o; q && q !== root; q = q.parent) if (!q.visible) return;   // (the root's own visibility aside: a hidden form still costs what it would)
    draws++;
    triangles += mesh.geometry.index ? mesh.geometry.index.count / 3 : mesh.geometry.attributes.position.count / 3;
  });
  return { triangles, draws };
}
